import { Box, Tooltip, Typography } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import { FC } from 'react'
import { useLocaleState, useTranslate } from 'react-admin'
import { border } from '../../layout/theme/border'
import { xxllncColor } from '../../layout/theme/color'
import { CapacityBlock, CapacityMetric } from '../../types/apiRequests'
import { formatCompact } from '../../utils/formatNumber'
import { fadeSlideIn } from './animations'

const fillBar = keyframes`
  from { transform: scaleX(0); }
`

interface State {
  color: string
  labelKey: string
}

const stateFor = (pct: number): State => {
  if (pct >= 85) return { color: xxllncColor.red100, labelKey: 'dashboard.capacity.atLimit' }
  if (pct >= 60) return { color: xxllncColor.orange100, labelKey: 'dashboard.capacity.busy' }
  return { color: xxllncColor.green100, labelKey: 'dashboard.capacity.healthy' }
}

const formatPeakAt = (peakAt: string | null | undefined, locale: string): string | null => {
  if (!peakAt) return null
  try {
    const d = new Date(peakAt)
    // E.g. "wo 14 mei 14:00" — short but unambiguous.
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  } catch {
    return null
  }
}

interface CapacityRowProps {
  label: string
  metric: CapacityMetric
  ceiling: number
  tooltipKey: string
  tooltipExtra: Record<string, string | number>
  delay: number
}

const CapacityRow: FC<CapacityRowProps> = ({
  label,
  metric,
  ceiling,
  tooltipKey,
  tooltipExtra,
  delay,
}) => {
  const translate = useTranslate()
  const [locale] = useLocaleState()

  const peak = metric.peak_per_hour ?? 0
  const avg = metric.avg_per_hour ?? 0
  const pct = ceiling > 0 ? Math.min(100, (peak / ceiling) * 100) : 0
  const avgPct = ceiling > 0 ? Math.min(100, (avg / ceiling) * 100) : 0
  const state = stateFor(pct)
  const displayPct = pct >= 10 ? Math.round(pct) : pct.toFixed(1).replace('.0', '')
  const peakAtFormatted = formatPeakAt(metric.peak_at ?? null, locale)

  const hasPeakData = peakAtFormatted !== null
  const tooltipText = hasPeakData
    ? translate(tooltipKey, {
      peak: formatCompact(peak),
      avg: formatCompact(avg),
      ceiling: formatCompact(ceiling),
      peakAt: peakAtFormatted,
      ...tooltipExtra,
    })
    : translate('dashboard.capacity.noDataTooltip')

  return (
    <Tooltip title={tooltipText} placement="left" arrow enterDelay={150}>
      <Box
        sx={{
          'animation': `${fadeSlideIn} 0.4s ease-out ${delay}ms both`,
          'cursor': 'help',
          'py': 0.5,
          'transition': 'background 0.15s',
          'mx': -1,
          'px': 1,
          'borderRadius': 1,
          '&:hover': { background: 'rgba(0,0,0,0.02)' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(0,0,0,0.55)',
              letterSpacing: '0.01em',
            }}
          >
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: state.color,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
              }}
            >
              {displayPct}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: state.color,
                lineHeight: 1,
              }}
            >
              %
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            position: 'relative',
            height: 3,
            background: 'rgba(0,0,0,0.05)',
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          {/* Ghost bar = average; the foreground bar = peak. Avg sits behind
              peak so visually the user reads the peak first and the avg
              becomes context. */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${avgPct}%`,
              background: state.color,
              opacity: 0.25,
              borderRadius: 999,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${pct}%`,
              background: state.color,
              borderRadius: 999,
              transformOrigin: 'left center',
              animation: `${fillBar} 0.9s cubic-bezier(0.16, 1, 0.3, 1) ${delay + 150}ms both`,
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            mt: 0.5,
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              color: 'rgba(0,0,0,0.3)',
              letterSpacing: '0.02em',
            }}
          >
            {translate(state.labelKey)}
          </Typography>
          {peakAtFormatted ? (
            <Typography
              sx={{
                fontSize: 10,
                color: 'rgba(0,0,0,0.4)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {peakAtFormatted}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Tooltip>
  )
}

interface CapacityCardProps {
  capacity?: CapacityBlock | null
  delay: number
}

/**
 * Compact platform-capacity widget.
 *
 * Reads everything from the dashboard endpoint's `capacity` block: peak +
 * average per hour over the last 7 days, plus the ceiling config (pods,
 * workflow duration, MCP ceiling) — all configurable via PortalSettings.
 * No hard-coded numbers live here.
 */
export const CapacityCard: FC<CapacityCardProps> = ({ capacity, delay }) => {
  const translate = useTranslate()

  if (!capacity) {
    return null
  }

  const { mcp, workflow, config } = capacity
  const pods = config.workflow_pods
  const avgWorkflowSeconds = config.avg_workflow_seconds

  const mcpCeiling = config.mcp_hour_ceiling
  const workflowCeiling = config.workflow_hour_ceiling

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: border.card.radius,
        border: '1px solid rgba(0,0,0,0.06)',
        px: 2.5,
        py: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        animation: `${fadeSlideIn} 0.5s ease-out ${delay}ms both`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            color: 'rgba(0,0,0,0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          {translate('dashboard.capacity.title')}
        </Typography>
        <Tooltip
          title={translate('dashboard.capacity.headerTooltip', {
            pods,
            seconds: avgWorkflowSeconds,
          })}
          placement="top"
          arrow
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              border: '1px solid rgba(0,0,0,0.18)',
              color: 'rgba(0,0,0,0.4)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 8,
              fontWeight: 700,
              cursor: 'help',
              lineHeight: 1,
            }}
          >
            i
          </Box>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          flex: 1,
          justifyContent: 'center',
        }}
      >
        <CapacityRow
          label={translate('dashboard.capacity.mcp')}
          metric={mcp}
          ceiling={mcpCeiling}
          tooltipKey="dashboard.capacity.mcpTooltip"
          tooltipExtra={{ pods }}
          delay={delay + 100}
        />
        <CapacityRow
          label={translate('dashboard.capacity.workflows')}
          metric={workflow}
          ceiling={workflowCeiling}
          tooltipKey="dashboard.capacity.workflowTooltip"
          tooltipExtra={{ pods, seconds: avgWorkflowSeconds }}
          delay={delay + 160}
        />
      </Box>
    </Box>
  )
}
